import { Embed } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigationContext } from '../../context/NavigationProvider';
import { showGlobalFilters } from '../../features/GlobalFilters';
import { useWindowSize } from '../../shared/useWindowSize';
import { Icon } from '../Icon';
import { LoadingBounce } from '../Loading';
import { ReportStatus, usePowerBIEmbed } from './usePowerBiEmbed';
import { usePowerBiFilters } from './usePowerBiFilters';

const MIN_DEVICE_WIDTH_FOR_REPORTS = 800;

export const PowerBiReport: React.FC = () => {
  const { t } = useTranslation();
  const { primaryFeature, secondaryFeature, tertiaryFeature } =
    useNavigationContext();
  const [clientWidth] = useWindowSize();

  const reportPage =
    tertiaryFeature?.reportPage ??
    secondaryFeature?.reportPage ??
    primaryFeature?.reportPage ??
    null;

  const {
    embedConfig,
    setEmbedClient,
    reportStatus,
    setReportPage,
    eventHandlers,
    reportHeight,
  } = usePowerBIEmbed();
  const { powerBiFilters } = usePowerBiFilters();

  const isShowingGlobalFilters = showGlobalFilters(
    secondaryFeature?.id || null,
    tertiaryFeature?.id || null,
  );

  const waitingForFilters = isShowingGlobalFilters && !powerBiFilters;

  useEffect(() => {
    setReportPage(reportPage);
  }, [reportPage, setReportPage]);

  return (
    <div className="relative">
      {clientWidth < MIN_DEVICE_WIDTH_FOR_REPORTS && (
        <div className="absolute bottom-0 left-0 right-0 top-0 z-20 mx-auto w-full items-center bg-white text-center">
          <div className="border-grey-300 mx-auto mb-4 mt-7 h-8 w-8 rounded-full border-2 border-dashed">
            <Icon name="ContentError" />
          </div>
          <span className="text-grey-600 font-light">
            {t('components:powerBiReport:warning:smallViewport')}
          </span>
        </div>
      )}
      {reportStatus !== ReportStatus.Rendered && (
        <div className="absolute bottom-0 left-0 right-0 top-0 z-10 flex justify-center bg-white">
          <LoadingBounce
            text={t(`components:powerBiReport:status:${reportStatus}`)}
          />
        </div>
      )}
      <div
        style={{
          height: `${reportHeight}px`,
          visibility:
            reportStatus === ReportStatus.Rendered ? 'visible' : 'hidden',
        }}
      >
        {embedConfig && reportPage && !waitingForFilters && (
          <PowerBIEmbed
            embedConfig={{
              ...embedConfig,
              pageName: reportPage,
              ...(powerBiFilters && isShowingGlobalFilters
                ? { filters: powerBiFilters }
                : {}),
            }}
            cssClassName="h-full"
            getEmbeddedComponent={(embedded: Embed) => setEmbedClient(embedded)}
            eventHandlers={eventHandlers}
          />
        )}
      </div>
    </div>
  );
};
